import { Navigate, useSearchParams } from "react-router-dom";
import { useMe } from "@/features/members/hooks/useMe";
import { REDIRECT_PARAM, safeRedirect } from "@/lib/redirects";
import { FullScreenLoader } from "@/components/Loader";

/**
 * Where signing in actually lands you.
 *
 * Clerk can only be given a fixed URL, and the right destination depends on
 * something Clerk does not know: whether this account is a member yet. So it
 * sends everybody here and this forwards them.
 *
 * - **Not a member** → the application, carrying wherever they were headed so
 *   that finishing it puts them back there. There is nothing else for them to
 *   do first, and landing on a directory preview with no idea how to join is
 *   how somebody signs up and never comes back.
 * - **A member with somewhere to be** → back to the page that asked them to
 *   sign in.
 * - **An admin who arrived from outside** → the admin dashboard, which is
 *   where their work is. Todd, after the 1 September demo: "logged in as
 *   admin, show dashboard by default." Only when nobody named a destination:
 *   an admin who clicked a link into the directory still lands on the
 *   directory.
 * - **Anybody else who arrived from outside** → the front page.
 */
export default function AfterSignInPage() {
  const [params] = useSearchParams();
  const { data: me, isPending } = useMe();

  if (isPending) {
    return (
      <FullScreenLoader label="Signing you in…" />
    );
  }

  const asked = params.get(REDIRECT_PARAM);
  // Only ever a path on this site, and the front page when nobody named one.
  const back = safeRedirect(asked);
  if (me && me.signedIn && me.isMember === false) {
    return (
      <Navigate
        to={asked ? `/apply?${REDIRECT_PARAM}=${encodeURIComponent(back)}` : "/apply"}
        replace
      />
    );
  }
  // An explicit destination always wins, so this only decides where somebody
  // arriving cold ends up.
  if (!asked && me?.member?.role === "admin") {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to={back} replace />;
}
